// ============================================================
// presence.ts — frontend client for Phase 1 case-level presence.
//
// A module-level singleton that owns ONE WebSocket to /ws/presence,
// authenticated with the bearer token. It:
//   - connects on login, reconnects with backoff on drop,
//   - tells the server which case is open (join / leave),
//   - exposes the viewer list for the CURRENTLY OPEN case to subscribers.
//
// ★ GRACEFUL DEGRADE: presence is purely additive. If the socket can't
// connect or drops, nothing throws and the app works normally — the
// viewer list just stays empty (no "also viewing" shown). Every send is
// guarded by readyState; failures are swallowed.
// ============================================================
import { BASE_URL } from '../api/casevoApi'
import { isOwnBillEcho } from './ownBillWrites'
import { TAB_ID } from './tabId'

// (D12) This tab's id — also the X-Casevo-Tab header on every API call.
export { TAB_ID, getTabId } from './tabId'
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((t): t is string => typeof t === 'string') : [])
const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)

// 'user-updated' payload (backend lib/presence.ts emitUserUpdated).
export interface PresenceUserUpdate {
  id: string
  firstName: string
  lastName: string
  avatarColour?: string | null
  avatarStyle?: string | null
  // Present only when the PHOTO changed: a presigned URL, or null = removed.
  avatarPhotoUrl?: string | null
}

export interface PresenceViewer {
  userId: string
  name: string
  avatarColour?: string
  avatarStyle?: 'solid' | 'soft' | 'icon'
  avatarPhotoUrl?: string | null
}
export interface PresenceSelf {
  userId: string
  name: string
  avatarColour?: string
  avatarStyle?: 'solid' | 'soft' | 'icon'
  avatarPhotoUrl?: string | null
}
// Phase 2a: one entry per user currently EDITING a field on the open
// case — who's focused on which field, + their display fields for the
// indicator avatar. Broadcast as a separate 'field-presence' channel.
export interface PresenceFieldOwner {
  userId: string
  fieldKey: string
  name: string
  avatarColour?: string
  avatarStyle?: 'solid' | 'soft' | 'icon'
  avatarPhotoUrl?: string | null
}
// Stage 1a live edit-sync: a remote user saved a field on blur and relayed
// its final value + the resulting SERVER updatedAt. Delivered to
// subscribeFieldEdits listeners for the CURRENTLY OPEN room only (the
// onmessage handler filters by activeRoomId, mirroring field-presence).
export interface PresenceFieldEdit {
  fieldKey: string
  value: unknown
  // The server updatedAt the SENDER's save produced — the receiver advances
  // its optimistic-concurrency baseline to this so its next save doesn't 409.
  updatedAt: string
}
// Stage A live form-field cursors: a remote user's caret position inside a text
// field. Transient/fire-and-forget (no persistence), delivered per open room and
// self-excluded. selStart/selEnd are integer offsets into the field's text; the
// overlay maps them to a pixel via a mirror measurement. Carries the sender's
// display fields so the cursor + avatar flag render without a separate lookup.
export interface PresenceFieldCaret {
  userId: string
  fieldKey: string
  selStart: number
  selEnd: number
  // The sender's FULL field value at the moment of this caret. Two uses on the
  // receiver: the overlay measures value[0..selStart] to position the cursor, and
  // an app-level receiver applies the whole value immediately so the TEXT updates
  // as fast as the cursor (rather than lagging on the ~400ms near-live path). The
  // field name is kept as `caretText` for wire compatibility with the relay.
  caretText: string
  name: string
  avatarColour?: string
  avatarStyle?: 'solid' | 'soft' | 'icon'
  avatarPhotoUrl?: string | null
}
// General firm-wide "entity changed" event — the SOLE live-freshness list
// channel (Stage 3 retired the old client-driven list-update relay). Server-
// originated (emitted by a controller after a successful create/update/delete)
// and fanned out to the whole firm. ECHO SUPPRESSION: the manager drops events
// whose `actorUserId` is the local user, so a subscriber only ever sees OTHER
// users' changes (the actor already applied its own). `at` is the new server
// updatedAt — receivers advance their optimistic-concurrency base to it so the
// next save doesn't spuriously 409. `scope` lets nested/room-scoped lists judge
// relevance; `summary` carries the lightweight display fields a list row needs.
export interface PresenceEntityChanged {
  entity: 'case' | 'client' | 'task' | 'activity' | 'deadline' | 'witness' | 'dayNote' | 'appointment' | 'clientNote'
  op: 'create' | 'update' | 'delete'
  id: string
  actorUserId: string
  at?: string
  scope?: { caseId?: string; clientId?: string }
  summary?: Record<string, unknown>
  // ★ Task access loss: the server sends a STRIPPED task update (id only, no
  // details) to a viewer who can no longer see the task → drop the row live.
  revoked?: boolean
}

// Live BILLING channel (backend lib/presence.ts emitBillChanged): "bill X on
// case Y changed". A refetch signal only — no figures. The SERVER only sends it
// to sockets allowed to see that case's bills, so receiving one never reveals a
// bill the user couldn't already open. Echo-suppressed per TAB only
// (services/ownBillWrites): the acting tab skips its own save's echo (it has
// the fresh bill from its response — re-reading it doubled every save), while
// the actor's OTHER tabs and every other user still receive it.
export interface PresenceBillChanged {
  billId: string
  caseId: string
  op: 'create' | 'update' | 'delete'
  kind: string
  actorUserId: string
}

// (E-sign fixes B — first slice of roadmap D12) "something on case X changed".
// The backend sends it only to sockets of the same firm currently joined to
// room `case:<id>` (they passed the case-access check on join). No case data:
// consumers re-read through the normal REST endpoints (hooks/useCaseLive).
// `topics` say what changed: 'settlement', 'esign', … (more with D12).
export interface PresenceCaseChanged {
  caseId: string
  topics: string[]
  // (D12) Who made the change (null = the system: a signer, the sealing job…)
  // and which browser tab (X-Casevo-Tab). This tab's own changes never reach
  // subscribers — it already shows them.
  actorUserId?: string | null
  originTab?: string | null
}
// (D12) A firm-wide list changed (cases, clients, tasks, calendar, billing,
// documents, settings, users, deadlineTypes). Own-tab echoes are dropped.
export interface PresenceFirmChanged {
  topics: string[]
  actorUserId: string | null
  originTab: string | null
}
// (D12) Something of THIS user's changed (e.g. ['notifications'] = the bell).
export interface PresenceUserChanged {
  topics: string[]
}

// Derive the ws URL from the REST base. Prod: https://…railway.app →
// wss://…railway.app. Dev (BASE_URL===''): the backend runs on :3001.
const wsBaseUrl = (): string => {
  if (BASE_URL) return BASE_URL.replace(/^http/, 'ws')
  if (typeof window === 'undefined') return ''
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${window.location.hostname}:3001`
}

// Collaborative-notes (Stage A): the WS URL for the co-hosted Hocuspocus
// server, which lives on the SAME backend origin as the presence WS, on path
// /collab (see backend lib/collab.ts). Reuses wsBaseUrl so prod/dev origin
// resolution stays identical to presence. Stage B's HocuspocusProvider will
// connect here with `${collabWsUrl()}` + the session token + doc name
// `client:<id>`. Exported for Stage B; nothing consumes it yet.
export const collabWsUrl = (): string => `${wsBaseUrl()}/collab`

type Listener = (viewers: PresenceViewer[]) => void
type FieldOwnersListener = (owners: PresenceFieldOwner[]) => void
type FieldEditListener = (edit: PresenceFieldEdit) => void
type FieldCaretListener = (caret: PresenceFieldCaret) => void
type EntityChangedListener = (event: PresenceEntityChanged) => void

class PresenceManager {
  private ws: WebSocket | null = null
  private token: string | null = null
  private self: PresenceSelf | null = null
  // The room this socket is currently in: `case:<id>` / `client:<id>` /
  // `task:<id>`, or null when in none. Generalised from the old case-only
  // activeCaseId.
  private activeRoomId: string | null = null
  private viewers: PresenceViewer[] = []
  private listeners = new Set<Listener>()
  // Phase 2a: field-owner state + its OWN listener channel, kept separate
  // from the viewer-list channel so the two update independently.
  private fieldOwners: PresenceFieldOwner[] = []
  private fieldOwnersListeners = new Set<FieldOwnersListener>()
  // Stage 1a: the fieldKey the LOCAL user currently has focused (or null).
  // Retained here (setFocusedField used to only SEND it) so the edit-sync
  // layer can exclude incoming edits for the field the user is still
  // editing — applying a remote value mid-edit would clobber their caret.
  private localFocusedField: string | null = null
  // Stage 1a: subscribers to incoming remote field-edits for the open room.
  private fieldEditListeners = new Set<FieldEditListener>()
  // Stage A live cursors: subscribers to incoming remote field-caret positions.
  private fieldCaretListeners = new Set<FieldCaretListener>()
  // General firm-wide entity-changed subscribers (live-freshness channel).
  private entityChangedListeners = new Set<EntityChangedListener>()
  // 'permissions-changed' (an admin changed this user's permissions / role /
  // the firm defaults) + every RE-connect (a change may have landed while the
  // socket was down). Subscribers re-fetch /api/auth/me.
  private permissionsChangedListeners = new Set<() => void>()
  // 'user-updated' — a firm member changed their display identity (name /
  // avatar). Subscribers patch the central firmUsers map.
  private userUpdatedListeners = new Set<(u: PresenceUserUpdate) => void>()
  // Live billing events + every RE-connect (events sent while the socket was
  // down are lost, so billing screens refetch current state on reconnect).
  private billChangedListeners = new Set<(e: PresenceBillChanged) => void>()
  private reconnectedListeners = new Set<() => void>()
  // (E-sign fixes B) 'case-changed' — refetch signals for an open case.
  private caseChangedListeners = new Set<(e: PresenceCaseChanged) => void>()
  // (D12) firm-changed / user-changed / live-resync + the socket's up/down state.
  private firmChangedListeners = new Set<(e: PresenceFirmChanged) => void>()
  private userChangedListeners = new Set<(e: PresenceUserChanged) => void>()
  private liveResyncListeners = new Set<() => void>()
  private connectionListeners = new Set<(connected: boolean) => void>()
  private connected = false
  private hasConnectedOnce = false
  private reconnectAttempts = 0
  private reconnectTimer: number | null = null
  private wantConnected = false
  // ★ BUG-C: iOS-reliable field-focus cleanup. On iOS Safari, navigating away
  // from a surface or backgrounding the tab does NOT reliably run React unmount
  // or fire `focusout`/`beforeunload`, so a field this user was editing keeps
  // showing them as its owner to co-editors until a refresh. `pagehide` and
  // `visibilitychange` (→ hidden) fire reliably on iOS, so we clear the local
  // field focus (broadcasting a field-blur) on those. Bound once so connect()
  // can add and disconnect() can remove the exact same references.
  private readonly onPageHide = (): void => { this.setFocusedField(null) }
  private readonly onVisibilityChange = (): void => {
    if (typeof document !== 'undefined' && document.hidden) this.setFocusedField(null)
  }
  private lifecycleBound = false

  // ── Public API ──────────────────────────────────────────
  connect(token: string, self: PresenceSelf): void {
    // ★ BUG B: ALWAYS refresh the stored identity. `self` carries the user's
    // DISPLAY fields (avatarColour / name / avatarStyle / avatarPhotoUrl), which
    // can arrive AFTER the socket first connected — e.g. on iPad the cached
    // authUser lacks avatarColour and getMe hydrates it a moment later, re-firing
    // usePresenceConnection's effect. If we early-returned without updating
    // this.self, joins would keep sending the stale (colourless) identity → the
    // user shows as the green default to everyone. So we refresh self first.
    const alreadyConnected = this.wantConnected && this.token === token && !!this.ws
    this.self = self
    if (alreadyConnected) {
      // Same session — no need to reopen the socket, but push the refreshed
      // identity to the server by re-joining the active room (the join handler
      // updates the stored display + re-broadcasts, so the fresh colour reaches
      // co-viewers immediately). Idempotent room-wise (server no-ops the room
      // change) — this exists purely to update the display.
      if (this.activeRoomId && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({ type: 'join', roomId: this.activeRoomId, display: this.self })
      }
      return
    }
    this.token = token
    this.wantConnected = true
    this.bindLifecycle()
    this.clearReconnect() // a pending retry would otherwise open a 2nd socket
    this.open()
  }

  disconnect(): void {
    this.wantConnected = false
    this.token = null
    this.self = null
    this.activeRoomId = null
    this.localFocusedField = null
    this.unbindLifecycle()
    this.clearReconnect()
    if (this.ws) {
      try { this.ws.close() } catch { /* ignore */ }
      this.ws = null
    }
    this.setConnected(false)
    this.setViewers([])
    this.setFieldOwners([])
  }

  // Tell the server which ROOM is open (`case:<id>` / `client:<id>` /
  // `task:<id>`, or null = none). Idempotent. Generalised from the old
  // case-only setActiveCase.
  setActiveRoom(roomId: string | null): void {
    if (this.activeRoomId === roomId) return
    // Leave the old room's viewer + field context immediately (clears both
    // local lists — a different room's owners must not linger).
    this.activeRoomId = roomId
    // Switching rooms ends any field focus held in the old room, so the
    // edit-sync exclusion doesn't wrongly suppress edits on the new surface.
    this.localFocusedField = null
    this.setViewers([])
    this.setFieldOwners([])
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    if (roomId) this.send({ type: 'join', roomId, display: this.self })
    else this.send({ type: 'leave' })
  }

  // Report that the local user focused a field (fieldKey) or blurred
  // (null). Display-only + best-effort — guarded by an open socket + an
  // active room, and silently no-ops otherwise, so a field's value
  // behaviour is entirely unaffected when presence is down.
  setFocusedField(fieldKey: string | null): void {
    // Always retain the local focus locally, EVEN when the socket is down —
    // the edit-sync exclusion check (getLocalFocusedField) must be correct
    // regardless of presence connectivity. The send below is the best-effort
    // presence signal; retention is unconditional.
    this.localFocusedField = fieldKey
    if (!this.activeRoomId) return
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    if (fieldKey) this.send({ type: 'field-focus', roomId: this.activeRoomId, fieldKey })
    else this.send({ type: 'field-blur', roomId: this.activeRoomId })
  }

  // Stage 1a: the fieldKey the local user currently has focused, or null.
  // Read by the edit-sync receive path to SKIP an incoming edit for the
  // field the user is mid-edit in (on-blur model: it reconciles on their
  // next blur / reload).
  getLocalFocusedField(): string | null {
    return this.localFocusedField
  }

  // Stage 1a: relay the local user's just-saved field value to co-editors
  // in the open room, carrying the SERVER updatedAt the save produced.
  // Best-effort + guarded (open socket + active room) exactly like the
  // presence signals — a no-op when the socket is down, so the field's own
  // save path is entirely unaffected by presence connectivity.
  sendFieldEdit(fieldKey: string, value: unknown, updatedAt: string): void {
    if (!this.activeRoomId) return
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    this.send({ type: 'field-edit', roomId: this.activeRoomId, fieldKey, value, updatedAt })
  }

  // Stage A live cursors: relay the local user's caret position in a text field
  // to co-viewers in the open room. Transient + best-effort + guarded (open
  // socket + active room) — a no-op when the socket is down, so the field itself
  // is unaffected. The server attaches our identity and excludes us on relay.
  sendFieldCaret(fieldKey: string, selStart: number, selEnd: number, caretText: string): void {
    if (!this.activeRoomId) return
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    this.send({ type: 'field-caret', roomId: this.activeRoomId, fieldKey, selStart, selEnd, caretText })
  }

  // (Stage 3) sendListUpdate removed — list live-updates are server-driven now
  // (the update/create/delete controllers emit entity-changed). See
  // subscribeEntityChanged below.

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn)
    fn(this.viewers)
    return () => { this.listeners.delete(fn) }
  }

  getViewers(): PresenceViewer[] {
    return this.viewers
  }

  // Phase 2a: subscribe to the field-owner list for the open case.
  subscribeFieldOwners(fn: FieldOwnersListener): () => void {
    this.fieldOwnersListeners.add(fn)
    fn(this.fieldOwners)
    return () => { this.fieldOwnersListeners.delete(fn) }
  }

  getFieldOwners(): PresenceFieldOwner[] {
    return this.fieldOwners
  }

  // Stage 1a: subscribe to incoming remote field-edits for the open room.
  // Unlike the viewer/owner channels there's no snapshot to replay — an
  // edit is a transient event — so the listener is called only on future
  // edits. Returns an unsubscribe fn.
  subscribeFieldEdits(fn: FieldEditListener): () => void {
    this.fieldEditListeners.add(fn)
    return () => { this.fieldEditListeners.delete(fn) }
  }

  // Stage A live cursors: subscribe to incoming remote caret positions for the
  // open room. Transient (no snapshot replay) — called only on future carets.
  subscribeFieldCaret(fn: FieldCaretListener): () => void {
    this.fieldCaretListeners.add(fn)
    return () => { this.fieldCaretListeners.delete(fn) }
  }

  // General firm-wide entity-changed subscription (Stage 0). Transient, no
  // snapshot replay. The manager has already ECHO-SUPPRESSED the local user's
  // own events (see the onmessage branch), so a subscriber only receives OTHER
  // firm users' create/update/delete events.
  // Fired when the server says this user's effective permissions may have
  // changed (live push) and on every socket RE-connect.
  subscribePermissionsChanged(fn: () => void): () => void {
    this.permissionsChangedListeners.add(fn)
    return () => { this.permissionsChangedListeners.delete(fn) }
  }

  // A firm member's display identity changed (PATCH /api/auth/me). Delivered
  // to the whole firm INCLUDING the actor's other tabs.
  subscribeUserUpdated(fn: (u: PresenceUserUpdate) => void): () => void {
    this.userUpdatedListeners.add(fn)
    return () => { this.userUpdatedListeners.delete(fn) }
  }

  // Live billing channel (see PresenceBillChanged).
  subscribeBillChanged(fn: (e: PresenceBillChanged) => void): () => void {
    this.billChangedListeners.add(fn)
    return () => { this.billChangedListeners.delete(fn) }
  }

  // Fired on every RE-connect (not the first open) — subscribers refetch
  // whatever live state they show, so nothing missed while offline lingers.
  subscribeReconnected(fn: () => void): () => void {
    this.reconnectedListeners.add(fn)
    return () => { this.reconnectedListeners.delete(fn) }
  }

  // (E-sign fixes B) "case X changed (topics)" — see PresenceCaseChanged.
  // Transient, no snapshot replay; pair it with subscribeReconnected (events
  // sent while the socket was down are lost) — hooks/useCaseLive does both.
  subscribeCaseChanged(fn: (e: PresenceCaseChanged) => void): () => void {
    this.caseChangedListeners.add(fn)
    return () => { this.caseChangedListeners.delete(fn) }
  }

  subscribeEntityChanged(fn: EntityChangedListener): () => void {
    this.entityChangedListeners.add(fn)
    return () => { this.entityChangedListeners.delete(fn) }
  }

  // (D12) A firm-wide list changed (see PresenceFirmChanged). Own tab skipped.
  subscribeFirmChanged(fn: (e: PresenceFirmChanged) => void): () => void {
    this.firmChangedListeners.add(fn)
    return () => { this.firmChangedListeners.delete(fn) }
  }

  // (D12) Something of this user's changed (['notifications'] = the bell).
  subscribeUserChanged(fn: (e: PresenceUserChanged) => void): () => void {
    this.userChangedListeners.add(fn)
    return () => { this.userChangedListeners.delete(fn) }
  }

  // (D12) The server may have missed sending us events (its relay
  // reconnected) → refetch everything on screen.
  subscribeLiveResync(fn: () => void): () => void {
    this.liveResyncListeners.add(fn)
    return () => { this.liveResyncListeners.delete(fn) }
  }

  // (D12) Is the live socket open right now? (Drives the stamp-poll fallback.)
  isConnected(): boolean {
    return this.connected
  }

  // (D12) Called with the current state at once, then on every change.
  subscribeConnection(fn: (connected: boolean) => void): () => void {
    this.connectionListeners.add(fn)
    fn(this.connected)
    return () => { this.connectionListeners.delete(fn) }
  }

  private setConnected(v: boolean): void {
    if (this.connected === v) return
    this.connected = v
    this.connectionListeners.forEach((fn) => fn(v))
  }

  // ── Internals ───────────────────────────────────────────
  // ★ BUG-C: attach the iOS-reliable page-lifecycle listeners once. Idempotent
  // (lifecycleBound guard) so repeated connect() calls don't stack listeners.
  private bindLifecycle(): void {
    if (this.lifecycleBound || typeof window === 'undefined') return
    this.lifecycleBound = true
    window.addEventListener('pagehide', this.onPageHide)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  private unbindLifecycle(): void {
    if (!this.lifecycleBound || typeof window === 'undefined') return
    this.lifecycleBound = false
    window.removeEventListener('pagehide', this.onPageHide)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  }

  private open(): void {
    const base = wsBaseUrl()
    if (!base || !this.token) return
    // ★ (Batch C) Never run two sockets: detach + close any previous one
    // first. Each handler below also ignores events from a socket that is no
    // longer `this.ws`, so a late onclose from an old socket can't null out
    // (or schedule a reconnect over) the live one.
    if (this.ws) {
      const old = this.ws
      old.onopen = null; old.onmessage = null; old.onclose = null; old.onerror = null
      try { old.close() } catch { /* ignore */ }
      this.ws = null
      this.setConnected(false)
    }
    try {
      const ws = new WebSocket(`${base}/ws/presence?token=${encodeURIComponent(this.token)}`)
      this.ws = ws
      ws.onopen = () => {
        if (this.ws !== ws) return
        this.reconnectAttempts = 0
        this.setConnected(true)
        // A RE-connect (not the first open): permissions may have changed while
        // we were offline and the push was missed → let subscribers re-fetch.
        if (this.hasConnectedOnce) {
          this.permissionsChangedListeners.forEach((fn) => fn())
          this.reconnectedListeners.forEach((fn) => fn())
        }
        this.hasConnectedOnce = true
        // Re-announce the open room after a (re)connect.
        if (this.activeRoomId) this.send({ type: 'join', roomId: this.activeRoomId, display: this.self })
        // …and the field we're focused in, so co-viewers' indicator returns.
        if (this.activeRoomId && this.localFocusedField) this.send({ type: 'field-focus', fieldKey: this.localFocusedField })
      }
      ws.onmessage = (ev) => {
        if (this.ws !== ws) return
        let msg: { type?: string; roomId?: string; viewers?: PresenceViewer[]; owners?: PresenceFieldOwner[]; fieldKey?: string; value?: unknown; updatedAt?: string; selStart?: number; selEnd?: number; caretText?: string; userId?: string; name?: string; avatarColour?: string; avatarStyle?: 'solid' | 'soft' | 'icon'; avatarPhotoUrl?: string | null; entity?: string; op?: string; id?: string; fields?: Record<string, unknown>; actorUserId?: string; at?: string; scope?: { caseId?: string; clientId?: string }; summary?: Record<string, unknown>; revoked?: boolean }
        try { msg = JSON.parse(ev.data) } catch { return }
        // Only surface lists / edits for the room we currently have open.
        if (msg.type === 'user-updated') {
          const u = (msg as { user?: PresenceUserUpdate }).user
          if (u && typeof u.id === 'string' && typeof u.firstName === 'string' && typeof u.lastName === 'string') {
            this.userUpdatedListeners.forEach((fn) => fn(u))
          }
          return
        }
        if (msg.type === 'bill-changed') {
          const b = msg as { billId?: unknown; caseId?: unknown; op?: unknown; kind?: unknown; actorUserId?: unknown }
          if (typeof b.billId === 'string' && typeof b.caseId === 'string' && (b.op === 'create' || b.op === 'update' || b.op === 'delete')) {
            // ECHO SUPPRESSION, per TAB: this tab's own save of this bill (it
            // already has the fresh bill from the response) — services/ownBillWrites.
            if (b.actorUserId === this.self?.userId && isOwnBillEcho(b.billId)) return
            const e: PresenceBillChanged = { billId: b.billId, caseId: b.caseId, op: b.op, kind: typeof b.kind === 'string' ? b.kind : '', actorUserId: typeof b.actorUserId === 'string' ? b.actorUserId : '' }
            this.billChangedListeners.forEach((fn) => fn(e))
          }
          return
        }
        if (msg.type === 'case-changed') {
          const c = msg as { caseId?: unknown; topics?: unknown; actorUserId?: unknown; originTab?: unknown }
          if (typeof c.caseId === 'string' && c.caseId) {
            const originTab = strOrNull(c.originTab)
            // (D12) This tab's own change — it already shows it.
            if (originTab && originTab === TAB_ID) return
            const e: PresenceCaseChanged = { caseId: c.caseId, topics: strings(c.topics), actorUserId: strOrNull(c.actorUserId), originTab }
            this.caseChangedListeners.forEach((fn) => fn(e))
          }
          return
        }
        if (msg.type === 'firm-changed') {
          const f = msg as { topics?: unknown; actorUserId?: unknown; originTab?: unknown }
          const originTab = strOrNull(f.originTab)
          if (originTab && originTab === TAB_ID) return
          const e: PresenceFirmChanged = { topics: strings(f.topics), actorUserId: strOrNull(f.actorUserId), originTab }
          this.firmChangedListeners.forEach((fn) => fn(e))
          return
        }
        if (msg.type === 'user-changed') {
          const e: PresenceUserChanged = { topics: strings((msg as { topics?: unknown }).topics) }
          this.userChangedListeners.forEach((fn) => fn(e))
          return
        }
        if (msg.type === 'live-resync') {
          this.liveResyncListeners.forEach((fn) => fn())
          return
        }
        if (msg.type === 'permissions-changed') {
          // Carries no data — subscribers re-read the authoritative values.
          this.permissionsChangedListeners.forEach((fn) => fn())
          return
        }
        if (msg.type === 'presence' && msg.roomId === this.activeRoomId && Array.isArray(msg.viewers)) {
          this.setViewers(msg.viewers)
        } else if (msg.type === 'field-presence' && msg.roomId === this.activeRoomId && Array.isArray(msg.owners)) {
          this.setFieldOwners(msg.owners)
        } else if (
          msg.type === 'field-edit' &&
          msg.roomId === this.activeRoomId &&
          typeof msg.fieldKey === 'string' &&
          typeof msg.updatedAt === 'string'
        ) {
          // Fan out the remote edit to subscribers. The room filter above
          // guarantees it belongs to the open surface; focused-field
          // exclusion + fieldKey→store mapping happen in the subscriber.
          const edit: PresenceFieldEdit = { fieldKey: msg.fieldKey, value: msg.value, updatedAt: msg.updatedAt }
          this.fieldEditListeners.forEach((fn) => fn(edit))
        } else if (
          msg.type === 'field-caret' &&
          msg.roomId === this.activeRoomId &&
          typeof msg.fieldKey === 'string' &&
          typeof msg.selStart === 'number' &&
          typeof msg.selEnd === 'number' &&
          typeof msg.userId === 'string'
        ) {
          // Live remote cursor (Stage A). Echo-suppress the local user (the
          // backend excludes the sender SOCKET, but a second tab of the same user
          // would still relay — never render your own caret over yourself).
          if (msg.userId === this.self?.userId) return
          const caret: PresenceFieldCaret = {
            userId: msg.userId,
            fieldKey: msg.fieldKey,
            selStart: msg.selStart,
            selEnd: msg.selEnd,
            caretText: typeof msg.caretText === 'string' ? msg.caretText : '',
            name: typeof msg.name === 'string' ? msg.name : '',
            avatarColour: msg.avatarColour,
            avatarStyle: msg.avatarStyle,
            avatarPhotoUrl: msg.avatarPhotoUrl,
          }
          this.fieldCaretListeners.forEach((fn) => fn(caret))
        } else if (
          msg.type === 'entity-changed' &&
          typeof msg.entity === 'string' &&
          typeof msg.op === 'string' &&
          typeof msg.id === 'string' &&
          typeof msg.actorUserId === 'string'
        ) {
          // General firm-wide entity-changed (Stage 0). ECHO SUPPRESSION: drop
          // the local user's own events — the actor already applied the change
          // (optimistic update / REST response), so re-applying would double-up
          // or flicker. Only OTHER users' changes reach subscribers.
          if (msg.actorUserId === this.self?.userId) return
          const event: PresenceEntityChanged = {
            entity: msg.entity as PresenceEntityChanged['entity'],
            op: msg.op as PresenceEntityChanged['op'],
            id: msg.id,
            actorUserId: msg.actorUserId,
            at: typeof msg.at === 'string' ? msg.at : undefined,
            scope: msg.scope,
            summary: msg.summary,
            revoked: msg.revoked === true ? true : undefined,
          }
          this.entityChangedListeners.forEach((fn) => fn(event))
        }
      }
      ws.onclose = (ev: CloseEvent) => {
        if (this.ws !== ws) return // a replaced socket — ignore
        this.ws = null
        this.setConnected(false)
        this.setViewers([])
        this.setFieldOwners([])
        if (!this.wantConnected) return
        if (ev.code === 4001) {
          // ★ (Batch C) The server REJECTED this session (deactivated, password
          // reset, 2FA setup required…). Re-validate over REST (subscribers
          // re-fetch /api/auth/me → a dead session logs out, a 2FA-pending one
          // lands on the setup screen) and retry only at the max backoff, so a
          // dead token never hammers the server.
          this.permissionsChangedListeners.forEach((fn) => fn())
          this.reconnectAttempts = Math.max(this.reconnectAttempts, 5)
        }
        this.scheduleReconnect()
      }
      ws.onerror = () => {
        // Let onclose handle reconnect; swallow so nothing surfaces.
        try { ws.close() } catch { /* ignore */ }
      }
    } catch {
      // Construction failed (bad URL etc.) — degrade silently, retry.
      if (this.wantConnected) this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnect()
    // Exponential backoff capped at 30s, so a downed server doesn't spin.
    const delay = Math.min(30000, 1000 * 2 ** this.reconnectAttempts)
    this.reconnectAttempts++
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      if (this.wantConnected) this.open()
    }, delay)
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) { window.clearTimeout(this.reconnectTimer); this.reconnectTimer = null }
  }

  private send(obj: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify(obj)) } catch { /* best-effort */ }
    }
  }

  private setViewers(v: PresenceViewer[]): void {
    this.viewers = v
    this.listeners.forEach((fn) => fn(v))
  }

  private setFieldOwners(o: PresenceFieldOwner[]): void {
    this.fieldOwners = o
    this.fieldOwnersListeners.forEach((fn) => fn(o))
  }
}

export const presence = new PresenceManager()
